import { useEffect, useRef, RefObject } from 'react';

interface SwipeHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

interface SwipeOptions {
  threshold?: number; // Minimum distance for swipe (default: 50px)
  timeout?: number; // Maximum time for swipe (default: 300ms)
}

/**
 * Hook for handling swipe gestures on touch devices
 * @param handlers - Object with swipe direction handlers
 * @param options - Configuration options
 * @returns ref - Ref to attach to the swipeable element
 */
export const useSwipeGesture = <T extends HTMLElement>(
  handlers: SwipeHandlers,
  options: SwipeOptions = {}
): RefObject<T | null> => {
  const { threshold = 50, timeout = 300 } = options;
  const elementRef = useRef<T | null>(null);
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStart.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStart.current.x;
      const deltaY = touch.clientY - touchStart.current.y;
      const deltaTime = Date.now() - touchStart.current.time;

      // Check if swipe was fast enough
      if (deltaTime > timeout) {
        touchStart.current = null;
        return;
      }

      // Determine swipe direction
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      // Horizontal swipe
      if (absX > absY && absX > threshold) {
        if (deltaX > 0 && handlers.onSwipeRight) {
          handlers.onSwipeRight();
        } else if (deltaX < 0 && handlers.onSwipeLeft) {
          handlers.onSwipeLeft();
        }
      }
      // Vertical swipe
      else if (absY > threshold) {
        if (deltaY > 0 && handlers.onSwipeDown) {
          handlers.onSwipeDown();
        } else if (deltaY < 0 && handlers.onSwipeUp) {
          handlers.onSwipeUp();
        }
      }

      touchStart.current = null;
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handlers, threshold, timeout]);

  return elementRef;
};

/**
 * Hook for pull-to-refresh functionality
 * @param onRefresh - Callback when pull-to-refresh is triggered
 * @param options - Configuration options
 */
export const usePullToRefresh = <T extends HTMLElement>(
  onRefresh: () => void | Promise<void>,
  options: { threshold?: number; disabled?: boolean } = {}
): RefObject<T | null> => {
  const { threshold = 80, disabled = false } = options;
  const elementRef = useRef<T | null>(null);
  const touchStart = useRef<number>(0);
  const pulling = useRef<boolean>(false);

  useEffect(() => {
    if (disabled) return;

    const element = elementRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger if scrolled to top
      if (element.scrollTop === 0) {
        touchStart.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStart.current === 0) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - touchStart.current;

      // If pulling down from top
      if (diff > 0 && element.scrollTop === 0) {
        pulling.current = true;
        // Could add visual feedback here
      }
    };

    const handleTouchEnd = async (e: TouchEvent) => {
      if (!pulling.current) return;

      const currentY = e.changedTouches[0].clientY;
      const diff = currentY - touchStart.current;

      if (diff > threshold) {
        // Trigger refresh
        await onRefresh();
      }

      touchStart.current = 0;
      pulling.current = false;
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh, threshold, disabled]);

  return elementRef;
};

/**
 * Hook for detecting long press gesture
 * @param onLongPress - Callback when long press is detected
 * @param options - Configuration options
 */
export const useLongPress = <T extends HTMLElement>(
  onLongPress: () => void,
  options: { delay?: number; disabled?: boolean } = {}
): RefObject<T | null> => {
  const { delay = 500, disabled = false } = options;
  const elementRef = useRef<T | null>(null);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (disabled) return;

    const element = elementRef.current;
    if (!element) return;

    const handleStart = () => {
      timerRef.current = setTimeout(() => {
        onLongPress();
      }, delay);
    };

    const handleEnd = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };

    // Support both mouse and touch
    element.addEventListener('mousedown', handleStart);
    element.addEventListener('touchstart', handleStart, { passive: true });
    element.addEventListener('mouseup', handleEnd);
    element.addEventListener('mouseleave', handleEnd);
    element.addEventListener('touchend', handleEnd, { passive: true });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      element.removeEventListener('mousedown', handleStart);
      element.removeEventListener('touchstart', handleStart);
      element.removeEventListener('mouseup', handleEnd);
      element.removeEventListener('mouseleave', handleEnd);
      element.removeEventListener('touchend', handleEnd);
    };
  }, [onLongPress, delay, disabled]);

  return elementRef;
};
